import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { addWorkspaceMember, getWorkspaceData } from "@services/api";
import type { Workspace, WorkspacePermissionWithAuthor } from "@/types/models";

interface ManageMembersModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

/**
 * Stage 3.5: dev-only direct-add member modal. Lists current
 * workspace_permissions and lets the workspace owner paste a UUID to
 * add a new member as Contributor or Owner.
 *
 * UI is deliberately minimal — no user search, no autocomplete — so the
 * privacy surface stays at zero. When an invite-based flow lands this
 * whole modal should be replaced rather than extended.
 */
export function ManageMembersModal({
  open,
  onClose,
  workspaceId,
}: ManageMembersModalProps) {
  const [members, setMembers] = useState<WorkspacePermissionWithAuthor[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<"contributor" | "owner">("contributor");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoadingMembers(true);
    setError(null);
    const result = await getWorkspaceData(workspaceId);
    setLoadingMembers(false);
    if (result.success) {
      const ws = result.data as Workspace;
      setMembers(ws.workspace_permissions ?? []);
    } else {
      setError(result.error.message);
    }
  };

  useEffect(() => {
    if (!open) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  const handleAdd = async () => {
    const trimmed = newEmail.trim();
    if (!trimmed) {
      setError("Email is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await addWorkspaceMember(
      workspaceId,
      { email: trimmed },
      newRole
    );
    setSubmitting(false);
    if (result.success) {
      setNewEmail("");
      setNewRole("contributor");
      await reload();
    } else {
      setError(result.error.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Workspace Members</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add a registered user by email. For onboarding strangers, share
          a workspace invite link from the admin panel's Members tab.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Current members
        </Typography>
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
            mb: 2,
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          {loadingMembers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No members loaded.
            </Typography>
          ) : (
            members.map((m) => (
              <Box
                key={m.workspace_permission_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.5,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2">
                    {m.member?.username || m.user_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    added by {m.author?.username ?? "—"} ·{" "}
                    {m.member?.email || m.user_id}
                  </Typography>
                </Box>
                <Chip
                  label={m.permission_name}
                  size="small"
                  color={m.permission_name === "owner" ? "primary" : "default"}
                />
              </Box>
            ))
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Add member
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            size="small"
            type="email"
            label="Email"
            placeholder="name@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={submitting}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="add-member-role-label">Role</InputLabel>
            <Select
              labelId="add-member-role-label"
              label="Role"
              value={newRole}
              onChange={(e) =>
                setNewRole(e.target.value as "contributor" | "owner")
              }
              disabled={submitting}
            >
              <MenuItem value="contributor">Contributor</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={submitting || !newEmail.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? "Adding…" : "Add member"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
